import { Sheet, Spinner, Badge } from "@ui";
import { useLogConversation } from "@agentic_system/api/logs";
import { ReadOnlyConversationView } from "./read-only-conversation-view";
import { Cpu, ArrowDownToLine, ArrowUpFromLine, DollarSign } from "lucide-react";

interface ConversationSheetProps {
  conversationId: number | null;
  onClose: () => void;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

function formatCost(cost: number): string {
  if (cost === 0) return "$0";
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(2)}`;
}

export function ConversationSheet({ conversationId, onClose }: ConversationSheetProps) {
  const { data, isLoading } = useLogConversation(conversationId);

  const title = data?.title || "Conversation";
  const subtitle = data
    ? `${data.user.name || data.user.email}${data.user.profile_type ? ` — ${data.user.profile_type}` : ""}${data.user.profile_label ? ` — ${data.user.profile_label}` : ""}`
    : undefined;

  return (
    <Sheet
      open={conversationId !== null}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width="2/3"
      footer={
        data ? (
          <div className="flex items-center gap-4 text-sm text-slate-600">
            <div className="flex items-center gap-1.5">
              <ArrowDownToLine className="w-3.5 h-3.5 text-slate-400" />
              <span>In: {formatNumber(data.usage.input_tokens)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ArrowUpFromLine className="w-3.5 h-3.5 text-slate-400" />
              <span>Out: {formatNumber(data.usage.output_tokens)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Cpu className="w-3.5 h-3.5 text-slate-400" />
              <span>Total: {formatNumber(data.usage.total_tokens)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5 text-slate-400" />
              <span>{formatCost(data.usage.cost_usd)}</span>
            </div>
            <div className="ml-auto">
              <Badge
                label={`${data.messages.length} message${data.messages.length !== 1 ? "s" : ""}`}
                color="default"
                size="sm"
              />
            </div>
          </div>
        ) : undefined
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : data ? (
        <ReadOnlyConversationView messages={data.messages} />
      ) : null}
    </Sheet>
  );
}
